/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import {
  portsColumns as baseColumns,
  Port,
  portsColumns,
  shapePortData,
} from "./PortData";
import { useNavigate } from "react-router-dom";
import { ReactComponent as IconEye } from "../../../shared/assets/Icons/Icon_Eye.svg";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { fetchPort, fetchPortList } from "./requests/request";
import { useAuth0 } from "@auth0/auth0-react";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import ProvisioningDetailsModal from "../Modals/ProvisioningDetails";
import { mapFetchedDataToPortDetails } from "kontrolEdge/constants/constants";
import { useDispatch } from "react-redux";
import { setLag } from "@redux/slices/kontrolEdge/portSlice";
import { useAppSelector } from "@redux/hooks";
import EmptyItems from "@shared/components/EmptyItems";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { makePortFields } from "./portFieldSpec";
import { ParsedFilter as OldParsed } from "kontrolEdge/helpers/advancedFilters";


interface MainPort {
  text: string;
}

const MainPort: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [portList, setPortList] = useState<Port[]>([]);
  const [rawPortData, setRawPortData] = useState<any>(null);
  const [portDetailsData, setPortDetailsData] = useState<any>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const [selectedPort, setSelectedPort] = useState<Port | null>(null);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [isFetchingList, setIsFetchingList] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const overlaySpinner = useProcessingOverlay(isFetchingList);
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const portFields = useMemo(
    () => makePortFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );
  const suggestedKeys = useMemo(() => {
    const headers = portsColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [portsColumns, isViewingAllCustomers]);

  //This function is to fetch the detail of the port and show it inside the provisoning detail modal
  const handlePortInfo = async (port: Port) => {
    setSelectedPort(port);
    dispatch(setLag(port.lag));
    setIsModalOpen(true);
    try {
      setIsFetchingDetails(true);
      const token = await getAccessTokenSilently();
      const portId = port.id;
      if (!portId) {
        return;
      }
      const response = await fetchPort(token, portId.toString(), tenantId);
      setRawPortData(response.data?.data);
      setPortDetailsData(mapFetchedDataToPortDetails(response.data?.data));
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching port details:", error);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFetchingList(true);
        const response = await fetchPortList(api, tenantId);
        const { status, data } = response;
        if (status >= 500 || data.success === false) {
          setFetchError(data.message || "Unknown server error");
          setPortList([]);
        }
        //Valid array payload
        else if (Array.isArray(data.data)) {
          setPortList(shapePortData(data.data));
        }
        //Some other unexpected shape
        else {
          setPortList([]);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        console.error("Error fetching table ports", err);
        setFetchError(err.message || "Network error");
        setPortList([]);
      } finally {
        setIsFetchingList(false);
        setHasFetched(true);
      }
    };
    fetchData();
  }, [getAccessTokenSilently, tenantId]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Port>>();
    for (const f of portFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [portFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of portFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [portFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Port>(normalizedFilters, portFields),
    [normalizedFilters, portFields]
  );

  const filteredData = useMemo(
    () => portList.filter(predicate),
    [portList, predicate]
  );

  // ------------------------------------------------------------------------------------
  // Override "Actions"
  // ------------------------------------------------------------------------------------
  const columns = useMemo<GenericTableColumn<Port>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: Port) => row.customer?.name ?? "",
        renderCell: (row: Port) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }

    return cols.map((col) => {
      if (col.header === "Port ID") {
        return {
          ...col,
          renderCell: (row: Port) => (
            <button
              onClick={() => {
                dispatch(setLag(row.lag));
                navigate(`/kontrol/port-details?portId=${row.id}`, {
                  state: { 
                    selectedPortRow: row, 
                    portIdString: row.portId,
                    portType: row.portType as 'physical' | 'virtual' | undefined, },
                });
              }}
              className="
					hover:text-semantic-text-brand 
					hover:underline
					focus:underline 
					active:underline
					font-medium
					underline
					text-semantic-text-brand
					"
            >
              {row.portId}
            </button>
          ),
        };
      }
      if (col.header === "Details") {
        return {
          ...col,
          renderCell: (row: Port) => (
            <button
              className="
					flex items-center justify-center
					border border-semantic-border-secondary
					rounded
					text-semantic-text-secondary
					hover:bg-semantic-state-active
					w-10 h-10
				"
              onClick={() => handlePortInfo(row)}
            >
              <IconEye className="w-6 h-6" />
            </button>
          ),
        };
      }
      return col;
    });
  }, [baseColumns, isViewingAllCustomers]);

  //Cases to handle the fetching and the displat of empty items
  // If data is being fetched, show only the overlay spinner.
  if (!hasFetched || isFetchingList) {
    return overlaySpinner;
  }
  //If there's an error in the request
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  //If no ports are returned, display the empty state.
  if (portList.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative">
      {overlaySpinner}
      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
        >
          <div className="w-[520px] m-5">
            <GenericSearchbar
              placeholder="Find by attribute or value"
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
            />
          </div>
          <div className="px-5 mb-5">
            <GenericTable
              columns={columns}
              data={filteredData}
              initialRowsPerPage={10}
              rowsPerPageOptions={[10, 20, 30]}
              width="100%"
              tableClassName="bg-semantic-bg-primary"
            />
          </div>
          {overlaySpinner}
        </GenericCard>
      </div>
      <ProvisioningDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        portDetailsData={portDetailsData}
        rawPortData={rawPortData}
        selectedPort={selectedPort}
        isLoading={isFetchingDetails}
      />
    </div>
  );
};

export default MainPort;
